import React, {useEffect, useState} from "react";

import Stockchart from "../../../components/Stockchart";

import {useDataWithRoute, useDebounce, useTitleFromComponent} from "../../../utils/hooks";
import {VsiBubble, VsiH4, VsiLgText} from "../../../styled/App.styled";
import {Button, ButtonGroup, Card, Col, Input, Row} from "reactstrap";
import Grid from "../../../components/Grid";
import {useSearchParams} from "react-router-dom";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {
    calculateWeightedGrowthRate, calculateWeightedGrowthRateDaily,
    calculateWeightedGrowthRateWeekly,
    forecastWithRateV2, forecastWithRateV3,
    formatStockChartConfig,
} from "../../../utils/Helpers";
import {dateFormatter, moneyFormatter, numberFormatter,} from "../../../utils/gridFormatters";
import {VsiSelectLabel} from "../../../styled/VsiLabeledSelect.styled";
import {GetAuth, PutAuth} from "../../../utils/Verbs";
import dayjs from "dayjs";

const chartTypes = [
    {value: "effective", label: "Effective", route: "effective"},
    {value: "physical", label: "Physical", route: "physical"},
];

export const budgetTrendDefs = [
    {
        field: "collection_date",
        headerName: "Collection Date",
        filter: "agDateColumnFilter",
        cellRenderer: dateFormatter,
    },
    {
        field: "total_cost",
        type: "numericColumn",
        headerName: "Cost",
        filter: "agNumberColumnFilter",
        cellRenderer: moneyFormatter,
    },
    {
        field: "budget",
        type: "numericColumn",
        headerName: "Budget",
        filter: "agNumberColumnFilter",
        cellRenderer: moneyFormatter,
    },
    {
        field: "used",
        type: "numericColumn",
        headerName: "Used Units",
        filter: "agNumberColumnFilter",
        cellRenderer: numberFormatter,
    },
];

export default function CloudBudgetTrend({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const {theme, client, periodId, user, getAccessTokenSilently} = useVsiAppContext();
    const [searchParams] = useSearchParams();

    const type = searchParams.get("type") || "effective";
    let defaultType =
        chartTypes.find(({value}) => type === value) || chartTypes[0];
    const [budget, setBudget] = useState(0.00);
    const [chartData, setChartData] = useState([]);
    const [formattedChartData, setFormattedChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState(null);
    const [weeklyRange, setWeeklyRange] = useState(6);
    const getRouteForSelection = () => {
        return `/api/v6/report/enterprise_cloud_trend_proc?client_id=${client.clientId}&period_id=${periodId}`;
    };

    const getBudgetHistory = async () => {
        try {
            const token = await getAccessTokenSilently();
            const budgetRecord = await GetAuth(
                `/api/v6/admin/cloud_budget_model/${client.clientId}`,
                token
            );
            return budgetRecord?.data?.[0].budget
        } catch (error) {
            return 0.0
        }
    };
    useEffect(() => {
        getBudgetHistory().then(budget => setBudget(budget));
    }, []);

    const [data] = useDataWithRoute(getRouteForSelection(defaultType));

    useEffect(() => {
        handleStateWithData(data?.recordset.map((m) => {
            return {...m, 'budget': budget}
        }) || [[], []], weeklyRange);
    }, [data, searchParams, budget, weeklyRange]);

    // useEffect(() => {
    //     if (debouncedMonthRangeValue) {
    //         handleStateWithData(data?.recordset);
    //     }
    // }, [debouncedMonthRangeValue]);

    const handleStateWithData = (data, weeklyRange) => {
        // This will give weekly growth
        const growthRates = calculateWeightedGrowthRateDaily(
            data.filter(f => f.row_num === "1" && dayjs(f.collection_date.replace('T', '').replace('Z', '')) >=
                dayjs(f.collection_date.replace('T', '').replace('Z', '')).add(-6, "week")),
            ["total_cost"],
            weeklyRange
        );
        const forecastedData = forecastWithRateV3(
            data,
            ["total_cost"],
            30,
            growthRates,
            "Days"
        );

        const newData = [...data, ...forecastedData.map(m => {
            return {...m, 'budget': budget}
        })];

        let config = formatStockChartConfig(newData, [
            {
                name: "Spending",
                field: "total_cost",
                zoneAxis: "x",
                zones: [
                    {
                        value: Date.parse(data?.[data.length - 1]?.collection_date?.split?.("T")?.[0]),
                    },
                    {
                        dashStyle: "Dash",
                    }
                ],
            },
            {name: "Budget", field: "budget"},
        ]);

        setChartConfig({
            ...chartConfig,
            yAxis: {
                title: {
                    text: "$ Dollars",
                },
            },
        });

        setChartData(newData);
        setFormattedChartData(config.series);
    };

    const onRangeChange = (e) => {
        const {value} = e.target;
        setWeeklyRange(parseInt(value));
    };

    const onFormSubmit = async () => {
        try {
            const planData = {
                clientId: client.clientId,
                cloudBudget: Number(budget),
            };
            const token = await getAccessTokenSilently();
            await PutAuth(
                `/api/v6/admin/cloud_budget_model/${client.clientId}`,
                planData,
                token
            );
        } catch (error) {
            console.error(error);
        }
    };

    const onUndoClick = async () => {
        try {
            const planData = {
                clientId: client.clientId,
                cloudBudget: 0,
            };
            const token = await getAccessTokenSilently();
            await PutAuth(
                `/api/v6/admin/cloud_budget_model/${client.clientId}`,
                planData,
                token
            );
        } catch (error) {
            console.error(error);
        }
        setBudget(0)
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{`Enterprise Cloud Budget Trend`}</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 p-2 d-flex"}>
                    <VsiLgText>Budget ($): </VsiLgText>
                    <Input
                        type="number"
                        name="budget"
                        onChange={(event) => {
                            setBudget(event.target.value);
                        }}
                        style={{width: '200px', textAlign: 'right', marginLeft: 10}}
                        bsSize={'sm'}
                        size={100}
                        value={budget}
                    />
                </div>
                <ButtonGroup>
                    <Button
                        color={"info"}
                        id={"reset-btn-plan"}
                        onClick={onUndoClick}
                    >
                        <i className={"fa-solid fa-rotate-left"}/>
                    </Button>
                    <Button
                        color={"success"}
                        id="save-btn-plan"
                        onClick={onFormSubmit}
                    >
                        <i className={"fa-solid fa-floppy-disk"}/>
                    </Button>
                </ButtonGroup>
            </VsiBubble>
            <VsiBubble autobotInfo={{secondaryMetric: [budget, weeklyRange]}}>
                <Stockchart
                    selectedDefaultZoom={0}
                    series={formattedChartData}
                    config={chartConfig}
                    formatData={"${point.y:,2f}"}
                    formatLabels={"${value:,f}"}
                    formatPoint={"{series.name}: <b>${point.y}</b>"}
                />
                <Row>
                    <Col
                        sm={{size: 4, offset: 4}}
                        className="d-flex flex-column mb-3 align-items-center"
                    >
                        <VsiSelectLabel htmlFor="monthlyRangeSlider">
                            {"Weekly Growth Rate " +
                                (weeklyRange > 0
                                    ? `(Last ${weeklyRange} week${
                                        weeklyRange !== "1" ? "s )" : " )"
                                    }`
                                    : `(No Data)`)}
                        </VsiSelectLabel>
                        <Input
                            type="range"
                            name="weeklyRangeValue"
                            min={1}
                            max={6}
                            style={{direction: "ltr"}}
                            onChange={onRangeChange}
                            value={weeklyRange}
                            className={"mb-auto p-2"}
                            id="monthlyRangeSlider"
                        />
                    </Col>
                </Row>
            </VsiBubble>
            <VsiBubble autobotInfo={{secondaryMetric: [budget, weeklyRange]}}>
                <Grid addStyle data={chartData} columnDefs={budgetTrendDefs}/>
            </VsiBubble>
        </Card>
    );
}
